import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Sidebar } from '../../components/Sidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  ArrowLeft, Loader2, IndianRupee, Calendar, Users, Sparkles, MapPin,
  Download, Share2, Save, Send, Check, Instagram, Trash2, AlertTriangle, FileCheck2,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtInr = (n) => (n == null || n === '' ? '₹0' : `₹${Number(n).toLocaleString('en-IN')}`);
const fmtNum = (n) => {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toLocaleString();
};

export const BrandCampaignPlanWorkspace = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);

  // Local working copy of form fields. Server is the source of truth; we
  // patch via PATCH only when the brand clicks Save (explicit) — except
  // for creator removals, which save immediately.
  const [form, setForm] = useState({
    name: '', objective: '', budget: '', timeline_start: '', timeline_end: '',
    categories: '', target_locations: '',
    num_reels: 0, num_stories: 0, num_ugc: 0,
    notes: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/campaign-plans/${id}`, { withCredentials: true });
      setPlan(data);
      setForm({
        name: data.name || '',
        objective: data.objective || '',
        budget: data.budget || '',
        timeline_start: data.timeline_start || '',
        timeline_end: data.timeline_end || '',
        categories: (data.categories || []).join(', '),
        target_locations: (data.target_locations || []).join(', '),
        num_reels: data.num_reels || 0,
        num_stories: data.num_stories || 0,
        num_ugc: data.num_ugc || 0,
        notes: data.notes || '',
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load plan');
      navigate('/brand/plans');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        objective: form.objective.trim() || null,
        budget: Number(form.budget) || 0,
        timeline_start: form.timeline_start || null,
        timeline_end: form.timeline_end || null,
        categories: form.categories.split(',').map((s) => s.trim()).filter(Boolean),
        target_locations: form.target_locations.split(',').map((s) => s.trim()).filter(Boolean),
        num_reels: Number(form.num_reels) || 0,
        num_stories: Number(form.num_stories) || 0,
        num_ugc: Number(form.num_ugc) || 0,
        notes: form.notes.trim() || null,
      };
      const { data } = await axios.patch(`${API_URL}/api/campaign-plans/${id}`, payload, { withCredentials: true });
      setPlan(data);
      toast.success('Plan saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const removeCreator = async (creatorId) => {
    if (!plan) return;
    const next = (plan.creator_ids || []).filter((cid) => cid !== creatorId);
    try {
      const { data } = await axios.patch(
        `${API_URL}/api/campaign-plans/${id}`,
        { creator_ids: next },
        { withCredentials: true }
      );
      setPlan(data);
      toast.success('Creator removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/campaign-plans/${id}/pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(plan?.name || 'campaign-plan').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch {
      toast.error('PDF download failed');
    }
  };

  const copyShareLink = () => {
    if (!plan?.share_token) return;
    const link = `${window.location.origin}/plans/share/${plan.share_token}`;
    navigator.clipboard.writeText(link).then(
      () => toast.success('Share link copied — anyone with it can view this plan (read-only)'),
      () => toast.error('Could not copy link'),
    );
  };

  const convertToBrief = async () => {
    if (!plan?.creator_ids?.length) {
      toast.error('Add at least one creator before submitting');
      return;
    }
    if (!window.confirm('Submit this plan as a campaign brief to StrawHat HQ?')) return;
    setConverting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/campaign-plans/${id}/convert-to-brief`,
        {},
        { withCredentials: true }
      );
      toast.success('Plan submitted as a brief');
      // Refresh plan to surface the "Submitted" state, then bounce to campaigns.
      await load();
      setTimeout(() => navigate(`/brand/campaigns`), 600);
      return data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Convert failed');
    } finally {
      setConverting(false);
    }
  };

  const isConverted = !!plan?.converted_to_campaign_id;
  const summary = plan?.summary || {};

  // Variance between budget vs estimated spend — used for a subtle warning chip.
  const variance = useMemo(() => {
    const budget = Number(form.budget) || 0;
    const spend = Number(summary.estimated_deliverable_spend) || 0;
    if (budget <= 0) return null;
    const diff = spend - budget;
    return { diff, pct: budget ? (diff / budget) * 100 : 0 };
  }, [form.budget, summary.estimated_deliverable_spend]);

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center bg-white dark:bg-[#0A0B0D]">
          <Loader2 className="animate-spin text-[#FF451A]" size={32} />
        </div>
      </div>
    );
  }
  if (!plan) return null;

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="brand-plan-workspace-page">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/brand/plans')}
            className="text-sm text-[#6B6B6B] dark:text-zinc-400 hover:text-white flex items-center gap-1 mb-6"
            data-testid="plan-back-button"
          >
            <ArrowLeft size={14} /> All plans
          </button>

          <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-1" data-testid="plan-title">
                {plan.name}
              </h1>
              {isConverted && (
                <span className="inline-flex items-center gap-1 mt-2 text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full bg-[#00C972]/10 text-[#00C972] border border-[#00C972]/20" data-testid="plan-submitted-badge">
                  <FileCheck2 size={10} /> Submitted as brief
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={copyShareLink} className="border border-black/[0.10] dark:border-white/10 bg-white/5 text-[#0F0F0F] dark:text-white hover:bg-white/10 rounded-full px-4 py-2 text-sm flex items-center gap-1.5" data-testid="plan-share-button">
                <Share2 size={13} /> Share link
              </Button>
              <Button onClick={downloadPdf} className="border border-black/[0.10] dark:border-white/10 bg-white/5 text-[#0F0F0F] dark:text-white hover:bg-white/10 rounded-full px-4 py-2 text-sm flex items-center gap-1.5" data-testid="plan-download-pdf-button">
                <Download size={13} /> PDF
              </Button>
              <Button onClick={save} disabled={saving || isConverted} className="border border-[#FF451A]/40 bg-[#FF451A]/10 text-[#FF451A] hover:bg-[#FF451A]/15 rounded-full px-4 py-2 text-sm flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="plan-save-button">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
              </Button>
              <Button
                onClick={convertToBrief}
                disabled={converting || isConverted}
                className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 py-2 font-semibold text-sm hover:bg-[#E63E17] shadow-[0_4px_20px_rgba(255,69,26,0.3)] flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="plan-convert-button"
              >
                {converting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                {isConverted ? 'Already submitted' : 'Submit as brief'}
              </Button>
            </div>
          </div>

          {/* Top-line summary card */}
          <div className="bg-gradient-to-br from-[#0F0F0F] to-[#0A0A0A] border border-black/[0.10] dark:border-white/10 rounded-2xl p-6 mb-6" data-testid="plan-summary-card">
            <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-4 font-mono">Campaign summary</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SummaryTile icon={IndianRupee} iconColor="#00C972" label="Budget"           value={fmtInr(form.budget)} testid="summary-budget" />
              <SummaryTile icon={IndianRupee} iconColor="#FFB800" label="Est. spend"        value={fmtInr(summary.estimated_deliverable_spend)} testid="summary-est-spend" />
              <SummaryTile icon={Users}       iconColor="#FF451A" label="Creators"         value={summary.total_creators ?? 0} testid="summary-creators" />
              <SummaryTile icon={Sparkles}    iconColor="#0055FF" label="Deliverables"     value={summary.total_deliverables ?? 0} testid="summary-deliverables" />
            </div>
            {variance && variance.diff > 0 && (
              <div className="mt-4 inline-flex items-center gap-2 text-xs text-[#FFB800] bg-[#FFB800]/10 border border-[#FFB800]/20 rounded-full px-3 py-1.5" data-testid="plan-over-budget-warning">
                <AlertTriangle size={12} />
                Estimated spend exceeds budget by {fmtInr(variance.diff)} ({variance.pct.toFixed(0)}%)
              </div>
            )}
          </div>

          {/* Two-column layout: planning form + creators */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Left: planning controls (2/3) */}
            <div className="lg:col-span-2 space-y-6">
              {/* Overview */}
              <Section title="Overview" testid="section-overview">
                <Field label="Plan name">
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    disabled={isConverted}
                    className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                    data-testid="field-name"
                  />
                </Field>
                <Field label="Objective">
                  <textarea
                    value={form.objective}
                    onChange={(e) => setForm({ ...form, objective: e.target.value })}
                    disabled={isConverted}
                    rows={2}
                    placeholder="What does success look like?"
                    className="w-full rounded-xl text-sm p-3 outline-none focus:border-[#FF451A]/40 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                    data-testid="field-objective"
                  />
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Field label="Budget (₹)">
                    <Input
                      type="number"
                      value={form.budget}
                      onChange={(e) => setForm({ ...form, budget: e.target.value })}
                      disabled={isConverted}
                      className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                      data-testid="field-budget"
                    />
                  </Field>
                  <Field label="Start date" icon={Calendar}>
                    <Input
                      type="date"
                      value={form.timeline_start}
                      onChange={(e) => setForm({ ...form, timeline_start: e.target.value })}
                      disabled={isConverted}
                      className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                      data-testid="field-start"
                    />
                  </Field>
                  <Field label="End date" icon={Calendar}>
                    <Input
                      type="date"
                      value={form.timeline_end}
                      onChange={(e) => setForm({ ...form, timeline_end: e.target.value })}
                      disabled={isConverted}
                      className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                      data-testid="field-end"
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Categories (comma-separated)">
                    <Input
                      value={form.categories}
                      onChange={(e) => setForm({ ...form, categories: e.target.value })}
                      disabled={isConverted}
                      placeholder="beauty, skincare, lifestyle"
                      className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                      data-testid="field-categories"
                    />
                  </Field>
                  <Field label="Target locations (comma-separated)">
                    <Input
                      value={form.target_locations}
                      onChange={(e) => setForm({ ...form, target_locations: e.target.value })}
                      disabled={isConverted}
                      placeholder="Mumbai, Delhi, Bangalore"
                      className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                      data-testid="field-locations"
                    />
                  </Field>
                </div>
              </Section>

              {/* Deliverable planner */}
              <Section title="Deliverable Planner" testid="section-deliverables">
                <div className="grid grid-cols-3 gap-3" data-testid="deliverable-planner">
                  <NumberStepper label="Reels"  value={form.num_reels}   onChange={(v) => setForm({ ...form, num_reels: v })} disabled={isConverted} testid="planner-reels" />
                  <NumberStepper label="Stories" value={form.num_stories} onChange={(v) => setForm({ ...form, num_stories: v })} disabled={isConverted} testid="planner-stories" />
                  <NumberStepper label="UGC"     value={form.num_ugc}     onChange={(v) => setForm({ ...form, num_ugc: v })} disabled={isConverted} testid="planner-ugc" />
                </div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  <div className="bg-white/5 rounded-xl p-3 border border-black/[0.10] dark:border-white/10">
                    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1 font-mono">Avg reel · avg story</div>
                    <div className="text-[#0F0F0F] dark:text-white font-mono">
                      {fmtInr(summary.avg_reel_price || 0)} · {fmtInr(summary.avg_story_price || 0)}
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 border border-black/[0.10] dark:border-white/10" data-testid="planner-est-spend">
                    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1 font-mono">Estimated spend (server-computed)</div>
                    <div className="text-[#0F0F0F] dark:text-white font-mono font-semibold">{fmtInr(summary.estimated_deliverable_spend || 0)}</div>
                  </div>
                </div>
              </Section>

              {/* Notes */}
              <Section title="Internal notes" testid="section-notes">
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  disabled={isConverted}
                  rows={3}
                  placeholder="Anything else your team should know. (Not shared with HQ unless objective is empty.)"
                  className="w-full rounded-xl text-sm p-3 outline-none focus:border-[#FF451A]/40 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                  data-testid="field-notes"
                />
              </Section>
            </div>

            {/* Right: coverage + creator list */}
            <div className="lg:col-span-1 space-y-6">
              <Section title="Coverage" testid="section-coverage">
                <CoverageBlock label="Categories" items={summary.categories_represented || []} accent="#FF451A" testid="coverage-categories" />
                <CoverageBlock label="Cities"     items={summary.cities_represented || []}     accent="#0055FF" icon={MapPin} testid="coverage-cities" />
                <CoverageBlock label="Languages"  items={summary.languages_represented || []}  accent="#00C972" testid="coverage-languages" />
              </Section>

              <Section title={`Creators (${plan.creators?.length || 0})`} testid="section-creators">
                {(plan.creators?.length || 0) === 0 ? (
                  <div className="text-xs text-[#6B6B6B] dark:text-zinc-500 mb-3">
                    No creators yet. Open a shortlist and use "Build Plan", or pick from{' '}
                    <Link to="/brand/discover" className="text-[#FF451A] hover:underline">Discovery</Link>.
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="plan-creators-list">
                    {plan.creators.map((c) => (
                      <div key={c.id} className="bg-white/5 border border-black/[0.10] dark:border-white/10 rounded-xl p-2.5 flex items-center gap-2.5" data-testid="plan-creator-row">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-[#0F0F0F] dark:text-white text-xs font-bold shrink-0">
                          {c.name?.[0]?.toUpperCase() || 'C'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-[#0F0F0F] dark:text-white truncate">{c.name}</div>
                          {c.instagram_handle && (
                            <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 inline-flex items-center gap-1">
                              <Instagram size={9} /> @{c.instagram_handle} · {fmtNum(c.instagram_followers)}
                            </div>
                          )}
                        </div>
                        <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-400 font-mono shrink-0">
                          {fmtInr(c.pricing_reel)}
                        </div>
                        {!isConverted && (
                          <button
                            onClick={() => removeCreator(c.id)}
                            className="text-[#6B6B6B] dark:text-zinc-500 hover:text-[#FF3B30] p-1 transition-colors shrink-0"
                            data-testid="plan-creator-remove-button"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryTile = ({ icon: Icon, iconColor, label, value, testid }) => (
  <div className="bg-[#0F0F0F]/70 border border-black/[0.08] dark:border-white/5 rounded-xl p-4" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1.5 font-mono">
      <Icon size={11} style={{ color: iconColor }} /> {label}
    </div>
    <div className="text-2xl font-bold text-[#0F0F0F] dark:text-white">{value}</div>
  </div>
);

const Section = ({ title, children, testid }) => (
  <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-4 font-mono">{title}</div>
    <div className="space-y-3">{children}</div>
  </div>
);

const Field = ({ label, icon: Icon, children }) => (
  <div>
    <label className="text-[10px] font-semibold uppercase tracking-wider block mb-1.5 flex items-center gap-1 font-mono text-[#0F0F0F] dark:text-white">
      {Icon && <Icon size={10} />} {label}
    </label>
    {children}
  </div>
);

const NumberStepper = ({ label, value, onChange, disabled, testid }) => (
  <div className="bg-white/5 border border-black/[0.10] dark:border-white/10 rounded-xl p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-2 font-mono">{label}</div>
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => onChange(Math.max(0, (Number(value) || 0) - 1))}
        disabled={disabled}
        className="w-7 h-7 rounded-lg bg-white/5 border border-black/[0.10] dark:border-white/10 text-[#0F0F0F] dark:text-white hover:bg-white/10 disabled:opacity-30"
        data-testid={`${testid}-dec`}
      >
        −
      </button>
      <Input
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(Math.max(0, parseInt(e.target.value || '0', 10)))}
        disabled={disabled}
        className="bg-transparent border-none text-[#0F0F0F] dark:text-white text-center font-mono text-lg w-full p-0"
        data-testid={`${testid}-input`}
      />
      <button
        type="button"
        onClick={() => onChange((Number(value) || 0) + 1)}
        disabled={disabled}
        className="w-7 h-7 rounded-lg bg-[#FF451A] text-[#0F0F0F] dark:text-white hover:bg-[#E63E17] disabled:opacity-30"
        data-testid={`${testid}-inc`}
      >
        +
      </button>
    </div>
  </div>
);

const CoverageBlock = ({ label, items, accent, icon: Icon, testid }) => (
  <div data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1.5 font-mono">{label}</div>
    {items.length === 0 ? (
      <div className="text-xs text-[#6B6B6B] dark:text-zinc-500">—</div>
    ) : (
      <div className="flex flex-wrap gap-1">
        {items.slice(0, 12).map((it) => (
          <span
            key={it}
            className="px-2 py-0.5 text-[11px] rounded-full font-medium inline-flex items-center gap-1"
            style={{ backgroundColor: `${accent}15`, color: accent, border: `1px solid ${accent}30` }}
          >
            {Icon && <Icon size={9} />} {it}
          </span>
        ))}
      </div>
    )}
  </div>
);

// keep eslint happy on the `Check` import even if unused above
export const __PlanCheck = Check;
